/**
 * Booking states — the ONE contract for "what state is this booking in" (W4).
 *
 * Two independent axes, never conflated:
 *
 *   1. LIFECYCLE — `booking.status`, shown via `lifecycleLabel`. A paid/solid
 *      booking (status 'confirmed', is_firm) is labelled "Booked". The status
 *      VALUE 'confirmed' is kept in the DB; only its label changed (DECIDED 23 Sep).
 *   2. CONFIRMATION — the call outcome, derived by `confirmationStateOf` from
 *      confirmedAt / confirmedOnCall / confirmationAttempts / opsEscalatedAt /
 *      short-notice / touches. "Confirmed" means ONLY this: the client said yes.
 *
 * Cadence (vault System Diagram 06, mirrored from apps/web confirmationWorklist):
 * appointment days pair up (Mon+Tue, Wed+Thu, Fri+Sat); three call sequences on
 * three straight working days (Sundays skipped), worked from a Thursday ~10 days
 * ahead; Ops decision the next working day. Short-notice (lead < 7 days) and
 * gap-fill (confirmed on the call) bookings skip it: "confirmed at booking".
 *
 * DECIDED rules encoded here:
 *   - Cadence scope = solid, live, pre-arrival bookings (plus legacy 'rescheduled').
 *   - A moved booking ALWAYS re-enters the cadence: it is never short-notice or
 *     confirmed-at-booking, and touches from before the move do not count.
 *     Writers reset the counters with `resetCadence`.
 *   - Call-confirming an unpaid hold records the call only (`applyCallConfirmed`
 *     never changes status); the hold stays held and `needsDeposit` is derived.
 *
 * Day maths runs in the process's local zone (TZ=Australia/Melbourne), exactly as
 * the web worklist does with date-fns. Floor day keys use the studio's fixed
 * +10:00 offset, matching apps/web/src/utils/studioDay.ts `dayKey`.
 */
import { isFirm } from './slotConflicts';

/** The booking fields these rules read. The web `Booking` fits structurally. */
export interface StateBooking {
  status: string;
  /** ISO instant of the appointment. */
  start: string;
  /** ISO instant the booking row was made (for this slot). */
  createdAt: string;
  overbookable?: boolean | null;
  confirmedAt?: string | null;
  confirmedOnCall?: boolean | null;
  confirmationAttempts?: number | null;
  opsEscalatedAt?: string | null;
  /** Set on the NEW row of a move. */
  rescheduledFromBookingId?: string | null;
  rescheduleHistory?: ReadonlyArray<{ at: string }> | null;
  confirmationTouches?: ReadonlyArray<{ at: string; outcome: string }> | null;
}

type StatusOnly = { status: string; overbookable?: boolean | null };

// ---------------------------------------------------------------------------
// Liveness + solidity
// ---------------------------------------------------------------------------

const DEAD_STATUSES: ReadonlySet<string> = new Set(['cancelled', 'no-show', 'superseded']);
const PRE_ARRIVAL: ReadonlySet<string> = new Set(['held', 'pending-deposit', 'confirmed', 'rescheduled']);
/** Legacy status: no longer written (a move keeps the lifecycle status), read as a live, booked slot. */
const LEGACY_RESCHEDULED = 'rescheduled';

/** Not cancelled, no-show or superseded. Legacy 'rescheduled' is live. */
export function isLiveBooking(b: StatusOnly): boolean {
  return !DEAD_STATUSES.has(b.status);
}

/** Mirrors the DB `is_firm` column exactly (firm status AND not overbookable). */
export function isSolid(b: StatusOnly): boolean {
  return isFirm({ status: b.status, overbookable: b.overbookable ?? undefined });
}

/**
 * Someone is booked and expected in (or is / was in) the chair: solid and live,
 * plus legacy 'rescheduled' rows (a moved, booked slot written before W4).
 */
export function isCommittedBooking(b: StatusOnly): boolean {
  if (!isLiveBooking(b)) return false;
  return isSolid(b) || b.status === LEGACY_RESCHEDULED;
}

/** Cadence scope: committed AND not yet arrived (confirmed, or legacy rescheduled). */
export function isInCadenceScope(b: StatusOnly): boolean {
  return isCommittedBooking(b) && PRE_ARRIVAL.has(b.status);
}

/** Pre-arrival statuses a deposit pay firms into `confirmed`. */
const PAY_FIRMS: ReadonlySet<string> = new Set(['held', 'pending-deposit', 'confirmed']);
/** Arrived statuses: a pay settles the deposit and firms the booking where it stands. */
const PAY_FIRMS_IN_PLACE: ReadonlySet<string> = new Set(['checked-in', 'in-chair']);

export type PayBookingEffect = 'firm' | 'firm-in-place' | 'refuse';

/**
 * What a deposit pay does to its booking (tv-2na.8.15, tv-2na.11.20), the ONE
 * rule the pay intents and the web mirror share:
 *   firm          - held / pending-deposit / confirmed -> confirmed + not over-bookable;
 *   firm-in-place - checked-in / in-chair: the client is here, so the status is
 *                   KEPT (checked-in -> confirmed is a legal correction step, so the
 *                   lifecycle alone would let a pay downgrade it) but the booking
 *                   stops being over-bookable, so an arrived hold that pays turns
 *                   solid and joins the Floor's Checked in column;
 *   refuse        - completed / cancelled / superseded / no-show / legacy rescheduled:
 *                   a pay never revives or rewrites a finished or dead booking.
 */
export function payBookingEffect(status: string): PayBookingEffect {
  if (PAY_FIRMS.has(status)) return 'firm';
  if (PAY_FIRMS_IN_PLACE.has(status)) return 'firm-in-place';
  return 'refuse';
}

/**
 * The booking fields a deposit pay writes, or undefined when it writes nothing:
 * firm -> confirmed + not over-bookable; firm-in-place -> the SAME status + not
 * over-bookable (only when it was still over-bookable); refuse -> nothing.
 */
export function payBookingWrite(b: StatusOnly): { status: string; overbookable: false } | undefined {
  const effect = payBookingEffect(b.status);
  if (effect === 'firm') return { status: 'confirmed', overbookable: false };
  if (effect === 'firm-in-place' && b.overbookable === true) return { status: b.status, overbookable: false };
  return undefined;
}

/** A pay firms this booking to `confirmed` (the web's old PAYABLE_BOOKING_STATUSES). */
export function payFirmsBooking(status: string): boolean {
  return payBookingEffect(status) === 'firm';
}

/** A move's new row (current model) or a legacy 'rescheduled' row. */
function isMoved(b: StateBooking): boolean {
  return Boolean(b.rescheduledFromBookingId) || b.status === LEGACY_RESCHEDULED;
}

function lastMoveAt(b: StateBooking): number | null {
  const hist = b.rescheduleHistory;
  if (!hist || hist.length === 0) return null;
  const ms = Date.parse(hist[hist.length - 1]!.at);
  return Number.isNaN(ms) ? null : ms;
}

// ---------------------------------------------------------------------------
// Studio calendar-day helpers. A "day" is the Melbourne wall-clock date of an
// instant (studioDayKey), carried as a UTC-midnight Date so day arithmetic never
// depends on the machine timezone (CI runs in UTC, the studios in AEST/AEDT).
// ---------------------------------------------------------------------------

function localDay(iso: string): Date {
  const [y, m, d] = studioDayKey(iso).split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!));
}

function plusDays(d: Date, n: number): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n));
}

function keyOf(d: Date): string {
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${d.getUTCFullYear()}-${mm}-${dd}`;
}

function calendarDaysBetween(laterIso: string, earlierIso: string): number {
  return Math.round((localDay(laterIso).getTime() - localDay(earlierIso).getTime()) / 86_400_000);
}

function addWorkingDays(d: Date, n: number): Date {
  let cur = d;
  for (let i = 0; i < n; i += 1) {
    cur = plusDays(cur, 1);
    if (cur.getUTCDay() === 0) cur = plusDays(cur, 1); // Sundays are not worked
  }
  return cur;
}

/** The studios' timezone. Every studio is in Victoria (AEST +10:00 / AEDT +11:00). */
export const STUDIO_TIME_ZONE = 'Australia/Melbourne';

const STUDIO_DAY_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: STUDIO_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Studio calendar day (YYYY-MM-DD) of an instant: the Melbourne wall-clock date, so it
 * follows daylight saving and never depends on the machine timezone. Accepts Z, offset
 * or date-only ISO strings (a date-only string is UTC midnight = 10:00/11:00 Melbourne,
 * the same date). The ONE day-key rule — web `dayKey` delegates here (tv-2na.10 m1).
 */
export function studioDayKey(iso: string): string {
  const parts = STUDIO_DAY_FMT.formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)!.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

const STUDIO_WALL_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: STUDIO_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

/** Melbourne wall-clock parts of an instant. */
function studioWallParts(ms: number): { y: number; mo: number; d: number; h: number; mi: number; s: number } {
  const parts = STUDIO_WALL_FMT.formatToParts(new Date(ms));
  const get = (type: string) => Number(parts.find(p => p.type === type)!.value);
  return { y: get('year'), mo: get('month'), d: get('day'), h: get('hour'), mi: get('minute'), s: get('second') };
}

/**
 * The instant at Melbourne wall time `hms` (HH:MM[:SS]) on studio day `day`,
 * independent of the host timezone and daylight saving (88v6 / 7fra).
 */
export function studioWallTimeToIso(day: string, hms: string): string {
  const [h = 0, mi = 0, s = 0] = hms.split(':').map(Number);
  const [y, mo, d] = studioDayKey(day).split('-').map(Number);
  const target = Date.UTC(y!, mo! - 1, d!, h, mi, s);
  let instant = target;
  for (let i = 0; i < 3; i += 1) {
    const p = studioWallParts(instant);
    const correction = target - Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s);
    if (!correction) break;
    instant += correction;
  }
  return new Date(instant).toISOString();
}

/** Midnight at the start of a studio calendar day, independent of host timezone. */
export function studioDayStartIso(day: string): string {
  return studioWallTimeToIso(day, '00:00:00');
}

/**
 * Studio day `day` at the same Melbourne time of day as `baseIso` — a pay-by
 * moved to another day keeps the link's cut-off time (88v6). Host-local
 * `setFullYear` shifted the day in a non-Melbourne browser.
 */
export function studioDayWithTimeOf(baseIso: string, day: string): string {
  const p = studioWallParts(Date.parse(baseIso));
  const pad = (n: number) => String(n).padStart(2, '0');
  return studioWallTimeToIso(day, `${pad(p.h)}:${pad(p.mi)}:${pad(p.s)}`);
}

/**
 * `ms` as a Melbourne wall-clock ISO string with its real offset
 * (`YYYY-MM-DDTHH:MM:SS+10:00`, or `+11:00` in daylight saving). Readers that
 * slice the string for the day / hour get the studio's, whatever the host zone;
 * the old fixed `+10:00` form was an hour out in daylight saving (7fra).
 */
export function studioLocalIso(ms: number): string {
  const p = studioWallParts(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  const offsetMin = Math.round((Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s) - Math.floor(ms / 1000) * 1000) / 60_000);
  const sign = offsetMin >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMin);
  return `${p.y}-${pad(p.mo)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}:${pad(p.s)}${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

// ---------------------------------------------------------------------------
// Cadence schedule + short-notice
// ---------------------------------------------------------------------------

export type ConfirmationPair = 'mon-tue' | 'wed-thu' | 'fri-sat';

/** Local day keys (YYYY-MM-DD) of the three sequence days and the Ops decision day. */
export interface CadenceSchedule {
  pair: ConfirmationPair;
  seq1: string;
  seq2: string;
  seq3: string;
  decision: string;
}

const PAIRS: readonly ConfirmationPair[] = ['mon-tue', 'wed-thu', 'fri-sat'];

/** Same derivation as web `confirmationSchedule(booking)`. */
export function cadenceSchedule(startIso: string): CadenceSchedule {
  const start = localDay(startIso);
  const dow = start.getUTCDay();
  const pairIndex = dow === 3 || dow === 4 ? 1 : dow === 5 || dow === 6 ? 2 : 0; // Sun falls back to Mon/Tue
  const weekMonday = plusDays(start, -((dow + 6) % 7));
  const baseThursday = plusDays(weekMonday, -11);
  const seq1 = addWorkingDays(baseThursday, pairIndex);
  const seq2 = addWorkingDays(seq1, 1);
  const seq3 = addWorkingDays(seq2, 1);
  const decision = addWorkingDays(seq3, 1);
  return { pair: PAIRS[pairIndex]!, seq1: keyOf(seq1), seq2: keyOf(seq2), seq3: keyOf(seq3), decision: keyOf(decision) };
}

/** Minimum lead (appointment day − booking-made day) to run the cadence. */
export const SHORT_NOTICE_LEAD_DAYS = 7;

/** Booked with less than SHORT_NOTICE_LEAD_DAYS of lead: no room for the cadence. */
export function isShortNotice(b: Pick<StateBooking, 'start' | 'createdAt'>): boolean {
  return calendarDaysBetween(b.start, b.createdAt) < SHORT_NOTICE_LEAD_DAYS;
}

// ---------------------------------------------------------------------------
// Confirmation state
// ---------------------------------------------------------------------------

export const CONFIRMATION_STATES = [
  'not-in-cadence',
  'waiting',
  'due-seq-1',
  'due-seq-2',
  'due-seq-3',
  'escalated',
  'confirmed',
  'confirmed-at-booking',
] as const;

export type ConfirmationState = (typeof CONFIRMATION_STATES)[number];

/**
 * The client has said yes on a call/message for THIS slot: `confirmedAt`, or a
 * touch with outcome 'confirmed' (for a moved booking, only touches at/after the
 * last move count — touches are history and travel with the row).
 */
export function isCallConfirmed(b: StateBooking): boolean {
  if (b.confirmedAt) return true;
  const touches = b.confirmationTouches ?? [];
  const since = isMoved(b) ? lastMoveAt(b) : null;
  return touches.some((t) => t.outcome === 'confirmed' && (since === null || Date.parse(t.at) >= since));
}

/**
 * Where a booking is in the confirmation cadence on the local day of `nowIso`.
 *
 *   not-in-cadence        dead (cancelled/no-show/superseded), or out of scope
 *                         (held, pending deposit, overbookable, already arrived)
 *                         and never call-confirmed.
 *   waiting               in the cadence; the next sequence day is not reached yet
 *                         (before seq 1, or today's sequence already attempted).
 *   due-seq-1|2|3         the next unattempted sequence's day has arrived
 *                         (rolls forward: stays due until attempted).
 *   escalated             3 unanswered sequences or an Ops flag; with Ops.
 *   confirmed             call-confirmed (any live booking — incl. a held one,
 *                         see needsDeposit — and arrived/completed ones).
 *   confirmed-at-booking  skips the cadence: gap-fill confirmed on the call, or
 *                         short-notice. Never for a moved booking.
 *
 * Unconfirmed on/after the appointment day keeps its due-seq state; the
 * Confirmations screen stops at the day before and the Floor shows it.
 */
export function confirmationStateOf(b: StateBooking, nowIso: string): ConfirmationState {
  if (!isLiveBooking(b)) return 'not-in-cadence';
  const moved = isMoved(b);

  if (!moved && b.confirmedOnCall) return 'confirmed-at-booking';
  if (isCallConfirmed(b)) return !moved && isShortNotice(b) ? 'confirmed-at-booking' : 'confirmed';
  if (!isInCadenceScope(b)) return 'not-in-cadence';
  if (!moved && isShortNotice(b)) return 'confirmed-at-booking';

  const attempts = Math.max(0, Math.floor(b.confirmationAttempts ?? 0));
  if (attempts >= 3 || b.opsEscalatedAt) return 'escalated';

  const s = cadenceSchedule(b.start);
  const dueDay = [s.seq1, s.seq2, s.seq3][attempts]!;
  const today = keyOf(localDay(nowIso));
  if (today >= dueDay) return (['due-seq-1', 'due-seq-2', 'due-seq-3'] as const)[attempts]!;
  return 'waiting';
}

/**
 * A call-confirmed booking that is not solid (held / pending deposit): the client
 * said yes but no deposit has made it firm. DECIDED: it stays held.
 */
export function needsDeposit(b: StateBooking): boolean {
  return isLiveBooking(b) && !isSolid(b) && (b.status === 'held' || b.status === 'pending-deposit') && isCallConfirmed(b);
}

/** Record a 'confirmed' call outcome. NEVER changes status (DECIDED 23 Sep). */
export function applyCallConfirmed<B extends StateBooking>(b: B, atIso: string): B {
  return { ...b, confirmedAt: atIso };
}

/**
 * A move re-enters the cadence: clear the call outcome and reset the counters;
 * the lifecycle status is kept. Apply to the NEW row of every move.
 */
export function resetCadence<B extends StateBooking>(b: B): B {
  return { ...b, confirmedAt: undefined, confirmedOnCall: undefined, confirmationAttempts: 0, opsEscalatedAt: undefined };
}

export type BadgeTone = 'cream' | 'gold' | 'blood' | 'mint';

export interface ConfirmationBadge {
  label: string;
  tone: BadgeTone;
}

const BADGES: Record<ConfirmationState, ConfirmationBadge> = {
  'not-in-cadence': { label: 'Unconfirmed', tone: 'cream' },
  waiting: { label: 'Awaiting call', tone: 'cream' },
  'due-seq-1': { label: '1st call due', tone: 'gold' },
  'due-seq-2': { label: '2nd call due', tone: 'gold' },
  'due-seq-3': { label: '3rd call due', tone: 'gold' },
  escalated: { label: 'With Ops', tone: 'blood' },
  confirmed: { label: 'Confirmed', tone: 'mint' },
  'confirmed-at-booking': { label: 'Confirmed at booking', tone: 'mint' },
};

/** Short label + tone the screens show for a confirmation state. */
export function confirmationBadge(state: ConfirmationState): ConfirmationBadge {
  return BADGES[state];
}

// ---------------------------------------------------------------------------
// Lifecycle label
// ---------------------------------------------------------------------------

export type LifecycleLabel =
  | 'Booked'
  | 'Held'
  | 'Pending deposit'
  | 'Checked in'
  | 'In chair'
  | 'Done'
  | 'Cancelled'
  | 'No-show'
  | 'Superseded'
  | 'Moved · no date';

const LIFECYCLE_LABELS: Record<string, LifecycleLabel> = {
  held: 'Held',
  'pending-deposit': 'Pending deposit',
  confirmed: 'Booked',
  rescheduled: 'Booked', // legacy: a moved, booked slot
  'checked-in': 'Checked in',
  'in-chair': 'In chair',
  completed: 'Done',
  cancelled: 'Cancelled',
  'no-show': 'No-show',
  superseded: 'Superseded', // the kept original of a move (history ghost)
};

/** Lifecycle label. Never "Confirmed" — that word is the call outcome only. */
export function lifecycleLabel(b: { status: string; rescheduledToBookingId?: string }): LifecycleLabel {
  if (b.status === 'superseded' && !b.rescheduledToBookingId) return 'Moved · no date';
  return LIFECYCLE_LABELS[b.status] ?? 'Held';
}

// ---------------------------------------------------------------------------
// Floor + piece progress
// ---------------------------------------------------------------------------

/** Today's committed appointments and clients already here, including unpaid arrivals. */
export function isOnTheFloor(b: StatusOnly & { start: string }, dayKey: string): boolean {
  const arrived = b.status === 'checked-in' || b.status === 'in-chair';
  return (arrived || isCommittedBooking(b)) && studioDayKey(b.start) === dayKey;
}

export interface PieceProgress {
  /** This session's number within the piece (1-based). */
  current: number;
  /** Planned total sessions, or null when not estimated. */
  total: number | null;
  /** "x of y" ("x of ?" when the total is unknown). */
  label: string;
}

/**
 * THE x-of-y: sessions completed so far + this one, of the total planned.
 * A completed booking is already counted in `sessionsCompleted`, so it is not
 * added again. (Replaces FloorManager's "+1 only when in-chair", which showed an
 * arriving client one session behind, and BookingDetailCard's sibling index.)
 * Accurate for the current/next session; a historical completed session reads
 * the piece's running count.
 */
export function pieceProgress(
  b: { status: string },
  piece: { sessionsCompleted?: number | null; estimatedTotalSessions?: number | null },
): PieceProgress {
  const done = Math.max(0, piece.sessionsCompleted ?? 0);
  const current = b.status === 'completed' ? Math.max(1, done) : done + 1;
  const total = piece.estimatedTotalSessions ?? null;
  return { current, total, label: `${current} of ${total ?? '?'}` };
}
